import React, {useCallback, useState} from "react";
import {ThinScrollbar, OutsideClickHandler} from "@reside/ui";
import {AdmissionFlag} from "@reside/reside-api-admission";
import {Manager, Popper, Reference} from "react-popper";
import {useHistory} from "react-router-dom";
import {useToggle} from "react-use";
import Alert from "react-s-alert";
import qs from "qs";

import {buildResponseErrorMessage} from "../../../../services/api/errorHelpers";
import {AdmissionsService} from "../../../../services/AdmissionsService";
import {Wrapper, Scrollable, SpinnerWrapper} from "./styles";
import {FlagList, FlagListHeader} from "../../../flag-list";
import {FlagCountIcon} from "../../../flag-count-icon";
import {Spinner} from "../../../spinner";

type Props = Readonly<{
  flagCount: number;
  admissionId: string;
  initialIsLoading?: boolean;
  initialIsOpen?: boolean;
  initialFlags?: ReadonlyArray<AdmissionFlag>;
}>;

export const FlagPopover = ({
  flagCount,
  admissionId,
  initialIsLoading = false,
  initialIsOpen = false,
  initialFlags,
}: Props) => {
  const [open, toggle] = useToggle(initialIsOpen);
  const [flags, setFlags] = useState(initialFlags);
  const [isLoading, setIsLoading] = useState(initialIsLoading);

  const history = useHistory();

  const onFlagItemClick = (flagId: string, sectionId: string) => {
    history.push({
      pathname: `/admin/workflows/${admissionId}/section/${sectionId}`,
      search: qs.stringify({flagId}),
    });
  };

  const onIconClick = useCallback(async () => {
    toggle(true);

    try {
      setIsLoading(true);
      const {data} = await AdmissionsService.getFlags(admissionId);

      setFlags(data);
    } catch (resp) {
      Alert.error(
        await buildResponseErrorMessage(
          resp,
          "Could not load flags for admission.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  }, [admissionId, toggle]);

  const closeOverlay = () => {
    toggle(false);
  };

  return (
    <Manager>
      <div>
        <Reference>
          {({ref}) => (
            <div
              ref={ref}
              onClick={flagCount > 0 ? onIconClick : undefined}
              data-dashboard-card-stop=""
              data-test-id={`admissionFlags-${admissionId}-${flagCount}`}
            >
              <FlagCountIcon count={flagCount} active={open} />
            </div>
          )}
        </Reference>
        {open && (
          <OutsideClickHandler onOutsideClick={closeOverlay}>
            <Popper placement="auto">
              {({ref, style}) => (
                <ThinScrollbar>
                  <Wrapper ref={ref} style={style}>
                    <FlagListHeader />
                    {flags && !isLoading ? (
                      <Scrollable>
                        <FlagList
                          onFlagItemClicked={onFlagItemClick}
                          flags={flags}
                        />
                      </Scrollable>
                    ) : (
                      <SpinnerWrapper>
                        <Spinner relative />
                      </SpinnerWrapper>
                    )}
                  </Wrapper>
                </ThinScrollbar>
              )}
            </Popper>
          </OutsideClickHandler>
        )}
      </div>
    </Manager>
  );
};
